import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import i18n from 'i18n-js';
import { buildUrl } from 'utils';
import Loading from 'components/Loading';
import Paginator from 'components/Paginator';
import { useHistory } from 'react-router-dom';
import { Card } from 'components/UI';
import { DocumentsActions } from 'state/modules/documents';
import { Box, Button, Typography } from '@mui/material';
import VersionStatus from 'components/Documents/VersionStatus';
import StarOutlinedIcon from '@mui/icons-material/StarOutlined';
import { styled } from '@mui/material/styles';

// --- Styled Components (Emotion/MUI v7) ---

const Container = styled(Box)({
  width: '100%',
  height: '100%',
});

const LoadingContainer = styled(Box)({
  height: 150,
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: 348,
});

const StyledButton = styled(Button)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(13),
  marginRight: 12,
  textTransform: 'capitalize',
  fontWeight: 500,
}));

const VersionElementBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2.25),
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  backgroundColor: '#F4F5F766',
  padding: '16px 12px',
}));

const VersionInfo = styled(Box)(({ theme }) => ({
  marginRight: theme.spacing(2.5),
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const VersionTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.subtitle,
  fontSize: theme.typography.pxToRem(13),
  fontWeight: theme.typography.fontWeightBold,
  maxWidth: 120,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const VersionLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.subtitle,
  fontSize: theme.typography.pxToRem(13),
  fontWeight: theme.typography.fontWeightBold,
}));

const VersionName = styled('span')(({ theme }) => ({
  fontSize: theme.typography.pxToRem(13),
  fontWeight: 400,
}));

const DefaultVersionIcon = styled(StarOutlinedIcon)(({ theme }) => ({
  color: '#FFB027',
  fontSize: theme.typography.pxToRem(13),
  marginLeft: 10,
  marginRight: 4,
}));

const DefaultVersionText = styled(Typography)(({ theme }) => ({
  color: '#FFB027',
  fontSize: theme.typography.pxToRem(11),
}));

const EmptyPlaceholder = styled(Box)(({ theme }) => ({
  color: theme.palette.text.subtitle,
  fontSize: 13,
}));

const VersionElement = ({ version, onClick }) => {
  const file = version?.files?.length ? version.files[0] : undefined;
  return (
    <VersionElementBox onClick={() => onClick(version.id)}>
      <VersionInfo>
        <Box display="flex" alignItems="center" mb={1}>
          <VersionTitle variant="subtitle2">{version.version}</VersionTitle>
          {version?.default && (
            <Box display="flex" flexDirection="row" alignItems="center">
              <DefaultVersionIcon />
              <DefaultVersionText variant="subtitle2">
                {i18n.t('documents.default_version')}
              </DefaultVersionText>
            </Box>
          )}
        </Box>
        {file ? (
          <VersionLabel noWrap={true} variant="subtitle2">
            {`${i18n.t('documents.file')}: `}
            <VersionName>{file?.filename || '--'}</VersionName>
          </VersionLabel>
        ) : (
          <VersionName>{i18n.t('documents.no_file_attached')}</VersionName>
        )}
      </VersionInfo>
      {version?.status !== 'NONE' && <VersionStatus status={version?.status} />}
    </VersionElementBox>
  );
};

VersionElement.propTypes = {
  version: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    version: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    files: PropTypes.array,
    default: PropTypes.bool,
    status: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
};

const VersionsListWidget = ({ documentId, menuItems = [] }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [params, setParams] = useState({
    page: 1,
    limit: 3,
  });

  const {
    fetchingVersions,
    versions: { list, pagination },
  } = useSelector((state) => state.documents);

  useEffect(() => {
    dispatch(DocumentsActions.fetchVersionsByDocumentId(documentId, params));
  }, [params, documentId, dispatch]);

  // Pagination
  const handleChangePage = (event, newPage) => {
    const newParams = { ...params, page: newPage + 1 };
    setParams(newParams);
  };

  // Version click
  const handleVersionClick = (versionId) => {
    history.push(buildUrl(`/documents/${documentId}/versions/${versionId}`));
  };

  // Title
  const title = (
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Typography color="textPrimary" component="span">
        <Box fontSize={14} fontWeight="fontWeightMedium" data-cy="versions-title">
          {i18n.t('documents.versions')}
        </Box>
      </Typography>
      <StyledButton
        type="button"
        variant="text"
        color="primary"
        disableFocusRipple={true}
        data-cy="view-all-versions"
        onClick={() => history.push(buildUrl(`/documents/${documentId}/versions`))}
      >
        {i18n.t('common.view_all')}
      </StyledButton>
    </Box>
  );

  return (
    <Card title={title} menuItems={menuItems} data-cy="versions-card" headerActionType="vertical">
      <Container data-cy="versions-list">
        {fetchingVersions ? (
          <LoadingContainer>
            <Loading />
          </LoadingContainer>
        ) : (
          <>
            {list?.length > 0 ? (
              <>
                <Box minHeight={295} data-cy="versions-items">
                  {list?.map((version) => (
                    <VersionElement
                      key={version.id}
                      version={version}
                      onClick={handleVersionClick}
                    />
                  ))}
                </Box>
                <Paginator
                  component="div"
                  limit={params?.limit}
                  pagination={pagination}
                  rowsPerPageOptions={[3]}
                  onPageChange={handleChangePage}
                />
              </>
            ) : (
              <Typography>
                <EmptyPlaceholder component="span">
                  {i18n.t(`documents.empty_versions`)}
                </EmptyPlaceholder>
              </Typography>
            )}
          </>
        )}
      </Container>
    </Card>
  );
};

VersionsListWidget.propTypes = {
  /**
   * Id of the document
   */
  documentId: PropTypes.string.isRequired,
  menuItems: PropTypes.array,
};

export default VersionsListWidget;
